import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDirectoryTerritoryUnits } from '../../../hooks/useDirectory';
import type {
  DirectoryCountryLevel,
  DirectoryTerritoryUnit,
} from '../../../types/directory.types';

/**
 * Control único «Ubicación» del directorio (F3E-3B).
 *
 * Sustituye a los dos selectores legacy (Departamento + Municipio) por una
 * navegación progresiva sobre el modelo territorial genérico:
 * `directory_territory_units()` para bajar un nivel por vez, y
 * `directory_territory_scope()` —que resuelve el Home— para filtrar.
 *
 * ⚠️ Una sola lógica para móvil y desktop. El mismo panel se presenta como
 * bottom sheet por debajo de `md` y como popover a partir de `md`: cambian las
 * clases, no el flujo ni el estado. No hay dos implementaciones que diverjan.
 *
 * ⚠️ Nada de jerarquía hardcodeada. Los nombres de nivel salen de
 * los niveles que declara el país (vía `directory_countries()`) y el nivel más profundo se
 * deriva de esa misma lista. No se asume «Departamento → Municipio → Distrito»
 * ni un número fijo de niveles.
 */

export interface SelectedTerritory {
  unitId: number;
  name: string;
}

interface LocationFilterProps {
  countryIso: string | null;
  countryName: string | null;
  levels: DirectoryCountryLevel[];
  selected: SelectedTerritory | null;
  onSelect: (unit: SelectedTerritory | null) => void;
  /** Deshabilitado mientras no hay país de contexto (F3E-2). */
  disabled?: boolean;
}

export default function LocationFilter({
  countryIso,
  countryName,
  levels,
  selected,
  onSelect,
  disabled = false,
}: LocationFilterProps) {
  const [open, setOpen] = useState(false);
  // Ruta visual dentro del panel. Es estado de NAVEGACIÓN y vive solo aquí: la
  // unidad aplicada la gobierna el Home. Cerrar el panel sin elegir no cambia
  // el filtro.
  const [path, setPath] = useState<DirectoryTerritoryUnit[]>([]);
  // Colocación del POPOVER de desktop. `null` = presentación de bottom sheet
  // (móvil), que se ancla al borde inferior y no necesita cálculo.
  const [popover, setPopover] = useState<{ arriba: boolean; maxAlto: number } | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const parent = path.length > 0 ? path[path.length - 1] : null;

  // Bajo demanda: solo se pide con el panel abierto, y una clave por
  // (país, padre) hace que volver atrás reutilice la cache sin nuevo request.
  const {
    data: units = [],
    isPending: unitsPending,
    isError: unitsError,
    refetch: refetchUnits,
  } = useDirectoryTerritoryUnits(countryIso, parent?.unitId ?? null, open);

  // Nivel más profundo declarado por el país. Sin niveles declarados no se puede
  // saber cuál es terminal: entonces toda unidad se ofrece como seleccionable
  // directa, que es lo honesto (nunca inventar descendientes).
  const maxLevel = useMemo(
    () => (levels.length > 0 ? Math.max(...levels.map((l) => l.level)) : null),
    [levels],
  );
  const esTerminal = (u: DirectoryTerritoryUnit) =>
    maxLevel == null || u.level >= maxLevel;

  // Etiqueta del nivel que se está listando, tomada del catálogo del país.
  const etiquetaNivel =
    units.length > 0
      ? levels.find((l) => l.level === units[0].level)?.label ?? null
      : null;

  const cerrar = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const abrir = () => {
    setPath([]); // cada apertura empieza en la raíz; la cache evita el coste
    setOpen(true);
  };

  const elegir = (unit: SelectedTerritory | null) => {
    onSelect(unit);
    setOpen(false);
    triggerRef.current?.focus();
  };

  // Escape cierra y devuelve el foco al disparador.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Click fuera (popover de desktop). En móvil cierra el backdrop, que es un
  // elemento propio: no dependemos de detectar el exterior de un panel que
  // ocupa la pantalla.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Bloqueo de scroll del fondo SOLO con el bottom sheet (móvil). En desktop el
  // popover no debe congelar la página.
  useEffect(() => {
    if (!open) return;
    if (typeof window === 'undefined' || !window.matchMedia) return;
    if (window.matchMedia('(min-width: 768px)').matches) return;
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previo;
    };
  }, [open]);

  // Foco dentro del panel al abrir y al cambiar de nivel.
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => {
      panelRef.current?.querySelector<HTMLElement>('button')?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open, path.length]);

  // El panel se declara `role="dialog" aria-modal="true"`, así que el foco debe
  // quedarse dentro mientras está abierto: sin esto, `Tab` seguía hasta las
  // tarjetas de médico del fondo (medido). Contención mínima —ciclar en los
  // extremos— en vez de una librería de focus trap.
  useEffect(() => {
    if (!open) return;
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const foco = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled') && el.getClientRects().length > 0);
      if (foco.length === 0) return;
      const primero = foco[0];
      const ultimo = foco[foco.length - 1];
      const activo = document.activeElement as HTMLElement | null;
      if (!panel.contains(activo)) {
        e.preventDefault();
        (e.shiftKey ? ultimo : primero).focus();
      } else if (e.shiftKey && activo === primero) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && activo === ultimo) {
        e.preventDefault();
        primero.focus();
      }
    };
    document.addEventListener('keydown', onTab, true);
    return () => document.removeEventListener('keydown', onTab, true);
  }, [open]);

  // El popover de desktop NO puede salirse del viewport. Se mide el hueco real
  // bajo el control y sobre él: si debajo no cabe el alto máximo y arriba hay
  // más sitio, se coloca encima; en cualquier caso el alto se recorta al hueco
  // disponible y la lista mantiene su scroll interno. El bottom sheet de móvil
  // no entra aquí: `popover` queda en `null` y conserva su anclaje inferior.
  useLayoutEffect(() => {
    if (!open) {
      setPopover(null);
      return;
    }
    const esDesktop = () =>
      typeof window !== 'undefined' &&
      !!window.matchMedia &&
      window.matchMedia('(min-width: 768px)').matches;

    const medir = () => {
      if (!esDesktop()) {
        setPopover(null);
        return;
      }
      const t = triggerRef.current?.getBoundingClientRect();
      if (!t) return;
      const SEPARACION = 8; // el mt-2 / mb-2 del panel
      const RESPIRO = 8; // margen contra el borde del viewport
      const TOPE = 320; // el max-h-80 original: no crece, solo se recorta
      const abajo = window.innerHeight - t.bottom - SEPARACION - RESPIRO;
      const arriba = t.top - SEPARACION - RESPIRO;
      const ponerArriba = abajo < TOPE && arriba > abajo;
      const disponible = Math.max(0, ponerArriba ? arriba : abajo);
      setPopover({ arriba: ponerArriba, maxAlto: Math.min(TOPE, disponible) });
    };

    medir();
    window.addEventListener('resize', medir);
    // `true`: también los scrolls de contenedores internos, no solo el de la página.
    window.addEventListener('scroll', medir, true);
    return () => {
      window.removeEventListener('resize', medir);
      window.removeEventListener('scroll', medir, true);
    };
  }, [open, path.length, units.length]);

  const resumen = selected ? selected.name : 'Todas las ubicaciones';
  const panelId = 'directory-location-panel';

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? cerrar() : abrir())}
        disabled={disabled}
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="dialog"
        className={`w-full flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 cursor-pointer text-left transition-colors ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
        }`}
      >
        <i className="ri-map-pin-line text-xl sm:text-2xl text-brand-purple"></i>
        <div className="flex-1 min-w-0">
          <label className="block text-sm sm:text-base font-semibold text-gray-900 mb-1 sm:mb-1.5">
            Ubicación
          </label>
          {/* Una sola línea con truncado: la ruta completa se ve dentro del
              panel, no permanentemente en el Home. */}
          <span className="text-sm sm:text-base text-gray-700 truncate block">
            {resumen}
          </span>
        </div>
        {selected ? (
          <span
            role="button"
            tabIndex={0}
            aria-label="Quitar filtro de ubicación"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onSelect(null);
              }
            }}
            className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-lg text-gray-400"></i>
          </span>
        ) : (
          <i
            className={`ri-arrow-down-s-line text-xl sm:text-2xl text-gray-400 transition-transform flex-shrink-0 ${
              open ? 'rotate-180' : ''
            }`}
          ></i>
        )}
      </button>

      {open && (
        // Móvil: capa a pantalla completa con backdrop + hoja inferior.
        // Desktop (md+): popover anclado al control, dentro del viewport.
        <div
          className={`fixed inset-0 z-50 md:absolute md:inset-auto md:left-0 md:right-0 md:z-50 ${
            popover?.arriba ? 'md:bottom-full md:mb-2' : 'md:top-full md:mt-2'
          }`}
        >
          <div
            className="absolute inset-0 bg-black/40 md:hidden"
            onClick={cerrar}
            aria-hidden="true"
          />
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            aria-modal="true"
            aria-label="Filtrar por ubicación"
            className="absolute inset-x-0 bottom-0 max-h-[80vh] flex flex-col bg-white rounded-t-2xl shadow-xl border border-gray-200 md:static md:max-h-80 md:rounded-xl"
            // Solo en desktop: `popover` es null con el bottom sheet, así que el
            // móvil conserva su `max-h-[80vh]` sin estilo en línea.
            style={popover ? { maxHeight: popover.maxAlto } : undefined}
          >
            {/* Cabecera: back + contexto + cierre */}
            <div className="flex items-center gap-2 px-4 sm:px-5 py-3 border-b border-gray-200 flex-shrink-0">
              {path.length > 0 && (
                <button
                  type="button"
                  onClick={() => setPath((p) => p.slice(0, -1))}
                  aria-label="Volver al nivel anterior"
                  className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className="ri-arrow-left-s-line text-2xl text-gray-500"></i>
                </button>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                  {parent ? parent.name : countryName ?? 'Ubicación'}
                </p>
                {/* Breadcrumb: aquí sí se ve la ruta completa. */}
                {path.length > 0 && (
                  <p className="text-xs text-gray-500 truncate">
                    {[countryName, ...path.map((u) => u.name)].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={cerrar}
                aria-label="Cerrar"
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-xl text-gray-500"></i>
              </button>
            </div>

            {/* Lista: scroll interno, el fondo no se mueve */}
            <div className="overflow-y-auto overscroll-contain flex-1">
              {/* «Todo X»: en la raíz limpia el filtro; dentro de una unidad la
                  selecciona entera (su scope incluye descendientes). */}
              <button
                type="button"
                onClick={() =>
                  elegir(parent ? { unitId: parent.unitId, name: parent.name } : null)
                }
                className="w-full px-4 sm:px-6 py-3 text-left text-sm sm:text-base font-medium text-brand-purple hover:bg-brand-mint/20 cursor-pointer transition-colors border-b border-gray-100"
              >
                {parent ? `Todo ${parent.name}` : `Todo ${countryName ?? 'el país'}`}
              </button>

              {unitsError && (
                <div className="px-4 sm:px-6 py-6 text-center">
                  <p className="text-sm text-gray-600 mb-3">
                    No pudimos cargar las ubicaciones.
                  </p>
                  <button
                    type="button"
                    onClick={() => refetchUnits()}
                    className="px-4 py-2 text-sm bg-brand-purple text-white font-semibold rounded-lg hover:bg-brand-purple-dark transition-colors cursor-pointer"
                  >
                    Reintentar
                  </button>
                </div>
              )}

              {!unitsError && unitsPending && (
                <div className="px-4 sm:px-6 py-6 space-y-2" aria-hidden="true">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="h-5 bg-gray-100 rounded animate-pulse" />
                  ))}
                </div>
              )}

              {!unitsError && !unitsPending && (
                <>
                  {etiquetaNivel && (
                    <p className="px-4 sm:px-6 pt-2 pb-1 text-[10px] sm:text-xs font-bold text-brand-purple uppercase tracking-wide bg-gray-50/60">
                      {etiquetaNivel}
                    </p>
                  )}
                  {units.map((u) =>
                    esTerminal(u) ? (
                      // Unidad terminal: se elige directamente, sin entrar.
                      <button
                        key={u.unitId}
                        type="button"
                        onClick={() => elegir({ unitId: u.unitId, name: u.name })}
                        className="w-full px-4 sm:px-6 py-2.5 sm:py-3 text-left text-sm sm:text-base text-gray-700 hover:bg-brand-mint/20 cursor-pointer transition-colors"
                      >
                        {u.name}
                      </button>
                    ) : (
                      // Unidad con nivel inferior: se entra. Si al entrar no
                      // devuelve hijos, el panel solo ofrece «Todo <unidad>»:
                      // sigue siendo seleccionable y no se inventan descendientes.
                      <button
                        key={u.unitId}
                        type="button"
                        onClick={() => setPath((p) => [...p, u])}
                        className="w-full px-4 sm:px-6 py-2.5 sm:py-3 flex items-center gap-2 text-left text-sm sm:text-base text-gray-700 hover:bg-brand-mint/20 cursor-pointer transition-colors"
                      >
                        <span className="flex-1 min-w-0 truncate">{u.name}</span>
                        <i className="ri-arrow-right-s-line text-xl text-gray-400 flex-shrink-0"></i>
                      </button>
                    ),
                  )}
                  {units.length === 0 && parent && (
                    <p className="px-4 sm:px-6 py-4 text-sm text-gray-500">
                      Esta ubicación no se divide en zonas más pequeñas.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
